import type { MouseEvent, FC } from 'react';
import React, { useRef, Fragment } from 'react';
import cn from 'classnames';
import { sendEvent } from '@alfa-bank/site-helpers-common/browser/send-event';
import useKeyPressEvent from 'react-use/lib/useKeyPressEvent';
import { useLockBodyScroll } from '@alfa-bank/site-use-lock-body-scroll';

import type { TPropsDesktop } from '../models';

export const Modal: FC<React.PropsWithChildren<TPropsDesktop>> = ({
    analytics,
    CloseIcon,
    IconAlfaLogo,
    Layout,
    Portal,
    Stack,
    alignCenter,
    children,
    contentOverflow,
    dataTestId = 'modal',
    fullHeight = false,
    id,
    isCloseIcon = true,
    logo,
    onClose = () => {},
    stackingOrder,
    size,
    styles,
    classNames = {},
}) => {
    useLockBodyScroll();
    const container = useRef<HTMLDivElement>(null);

    const handleClose = () => {
        if (analytics) {
            sendEvent(analytics);
        }

        onClose();
    };

    const handleCloseClickOutside = (event: MouseEvent<HTMLDivElement, globalThis.MouseEvent>) => {
        // Таким образом реализуется закрытие модалки по клику вне контента модального окна
        if (event.target === container.current) {
            handleClose();
        }
    };

    useKeyPressEvent('Escape', onClose);

    const renderCloseIcon = () =>
        isCloseIcon ? (
            <button
                className={styles.closeButton}
                data-test-id="modalCloseButton"
                onClick={handleClose}
                type="button"
            >
                <CloseIcon className={cn(styles.external, styles.closeIcon)} />
            </button>
        ) : null;

    const renderLogoIcon = () =>
        logo ? (
            <div className={styles.logo}>
                <IconAlfaLogo className={cn(styles.external, styles.logoIcon)} />
            </div>
        ) : null;

    const renderModalBody = () => {
        if (size) {
            const isFullwidth = size === 'fullwidth';
            const Wrapper = isFullwidth ? Layout : Fragment;

            return (
                <Wrapper>
                    <div
                        className={cn(
                            styles.contentWrapper,
                            styles.contentWrapperWithSize,
                            styles[size],
                            {
                                [styles.contentWrapperCenter]: alignCenter,
                            },
                            classNames.wrapper
                        )}
                    >
                        {renderLogoIcon()}
                        {renderCloseIcon()}

                        <div
                            className={cn(
                                styles.content,
                                styles.contentWithSize,
                                styles[size],
                                classNames.content,
                                {
                                    [styles.contentOverflow]: contentOverflow,
                                }
                            )}
                            data-test-id="modalWrapper"
                        >
                            {children}
                        </div>
                    </div>
                </Wrapper>
            );
        }

        return (
            <div
                className={cn(styles.contentWrapper, {
                    [styles.contentWrapperFullHeight]: fullHeight,
                })}
            >
                {renderLogoIcon()}
                {renderCloseIcon()}

                <div className={cn(styles.content)} data-test-id="modalWrapper">
                    {children}
                </div>
            </div>
        );
    };

    return (
        <Stack value={stackingOrder.OVERLAY}>
            {(zIndex: number) => (
                <Portal>
                    <div
                        ref={container}
                        className={cn(styles.modal, {
                            [styles.modalWithSize]: !!size,
                        })}
                        data-test-id={dataTestId}
                        id={id}
                        onClick={handleCloseClickOutside}
                        style={{ zIndex }}
                    >
                        {renderModalBody()}
                    </div>
                </Portal>
            )}
        </Stack>
    );
};
